/* step6ForecastFold.js -- THE Step-6 forecast fold: one vendor object from the Forecast Wizard (or the vendor-forecast
 * file, which builds the same object through utils/forecastWizardRows.js) folded into the staged Step-6 vendor row, in
 * the SNAKE shape the Step-6 import reads.
 *
 * MOVED HERE UNCHANGED from pages/AdminPage.js (2026-10-29, the vendor-forecast file): the body of
 * step6SaveForecastRows' per-vendor map, and the two predicates it shares with the import payload builder. The file
 * upload's change list is computed by calling THIS function on the staged row -- so what the change list shows is what
 * Apply folds, by construction, not by a second mapping. AdminPage imports all three; nothing changed behaviour (the
 * comments were made ASCII). */
import { buildVendorForecastMd } from './vendorForecast';

const r2 = n => Math.round(n * 100) / 100;

/* THE FORECAST/HISTORICAL SEAM -- ONE implementation, two callers (the wizard-save fold and the import payload
 * builder). close_month is the boundary the ENGINE uses (the Axis-2 gate tests `m > cutoff`; the prepaid amort books
 * `m > cutoff`). FALLBACK, not a silent no-op: firstForecastMonth can resolve from a STORED forecast_start with
 * close_month still null, and a guard that quietly stops firing is the failure class this repo keeps filing. */
export const isForecastMonthFor = (m, closeMonth, forecastMonths) =>
    (closeMonth ? m > closeMonth : (forecastMonths || []).includes(m));

/* THE TYPES WHOSE FORECAST THE ENGINE DERIVES, so a stored forecast cell can never be their source of truth:
 * prepaid <- prepaid_contracts (runFullModel section 12b), pct_revenue <- rate x source line, per_seat <- cost x
 * headcount (computeVendor, which never reads monthlyData for m > cut). */
export const ENGINE_DERIVED_FORECAST = new Set(['prepaid', 'pct_revenue', 'per_seat']);

/* "THE STORED FORECAST CELLS ON THIS ROW CANNOT BELONG TO ITS CURRENT TYPE." ONE predicate, TWO callers -- the
 * wizard-save fold (so the preview never shows a series the import would delete) and the import payload builder (the
 * 7a guard). one_off is NOT in the engine-derived set: a one_off vendor's forecast cells ARE its per-month manual
 * amounts. They are kept when the row was LOADED as one_off and dropped when they were inherited from a different type.
 * AN ABSENT loadedType COUNTS AS "not authored under one_off" -- the entry-mode case. */
export const inheritedForecastIsStale = (type, loadedType) =>
    ENGINE_DERIVED_FORECAST.has(type) || (type === 'one_off' && loadedType !== 'one_off');

/**
 * Fold ONE wizard vendor object `o` into the staged Step-6 vendor `v` -> the next staged row.
 * @param {object} v  the staged step6Vendors row (snake shape)
 * @param {object} o  the vendor object ForecastWizard's buildVendorObj produced (camel config on a spread of the row)
 * @param {object} c  { firstForecast, closeMonth, forecastMonths }
 */
export function foldWizardObjIntoVendor(v, o, { firstForecast, closeMonth, forecastMonths }) {
    const next = {
        ...v,
        forecast_type: o.type,
        billing_frequency: o.freq || 'monthly',
        monthly_amount: Number(o.amount) || 0,
        pct_rate: Number(o.pctRate) || 0,
        pct_revenue_source: o.pctRevenueSource || null,
        per_seat_cost: Number(o.perSeatCost) || 0,
        seat_basis: o.seatBasis || 'fte',
        seat_departments: o.seatDepartments || [],
        start_date: o.startDate || v.start_date,
        end_date: o.endDate || 'until_canceled',
        /* The wizard resolves a non-monthly cadence to a concrete first booking month. `vendors` has no column for it --
         * the anchor is expressed by WHICH forecast months carry the amount -- so it rides on the row for the import's
         * synthesis and is never persisted as a field. */
        billing_anchor: o.billingAnchor || null,
    };

    /* FOLD THE SYNTHESIZED FORECAST SERIES, NOT JUST THE CONFIG.
     *
     * THE BUG THIS CLOSES: `isDone` asks a constant row ONE question -- does it carry a FORECAST-month entry in
     * monthlyData? The Vendor Dashboard host answers it on save (it builds the series and persists it); this host
     * folded CONFIG ONLY, so a constant row could never be "done" here: the badge stayed TODO, `buildRows` re-ran the
     * 3-mo-avg PREFILL on re-open and DISCARDED the saved amount, and a second Save wrote those prefills back over the
     * good values -- a silent revert of the whole entry pass.
     * SAME FUNCTION, NOT A SECOND SYNTHESIZER. `buildVendorForecastMd` is the ONE builder the Dashboard host and the
     * import both call; this produces the artifact the component already documents as its input. No DB write -- the
     * import still persists, and it re-derives the identical series from the identical config.
     * `actualsMonths = []` for the same reason the import passes it: the historical half is carried here verbatim, and
     * the workbook holds months outside the model window that the helper's preserve-loop would drop.
     * NON-CONSTANT TYPES GET `{}` from the helper (its type gate), so prepaid / pct_revenue / per_seat gain nothing.
     * AND THE PREVIEW MUST NOT SHOW A SERIES THE IMPORT WOULD DELETE: cells that cannot belong to the row's CURRENT
     * type are dropped here through the SAME predicate the import guard uses, so a type switch inside the wizard cannot
     * leave a stale series behind lighting up a false "Set" badge. */
    const _md = { ...(v.monthly_amounts || {}) };
    if (inheritedForecastIsStale(next.forecast_type, v._loadedType)) {
        for (const m of Object.keys(_md)) {
            if (isForecastMonthFor(m, closeMonth, forecastMonths)) delete _md[m];
        }
    }
    const _fc = buildVendorForecastMd(
        {
            type: next.forecast_type,
            monthly_amount: next.monthly_amount,
            billing_frequency: next.billing_frequency,
            start_date: next.start_date || firstForecast,
            end_date: next.end_date,          // the row's REAL service window (see the import)
            billingAnchor: next.billing_anchor || undefined,
            renewals: v.renewals || [],   // Batch 1b (L3613): a loaded row's stored price changes, as the import applies them
            monthlyData: v.monthly_amounts || {},
        },
        [],
        forecastMonths,
        { startFallback: firstForecast }
    );
    for (const [m, amt] of Object.entries(_fc)) _md[m] = r2(Number(amt) || 0);
    next.monthly_amounts = _md;
    return next;
}
